import { useEffect, useId, useRef, useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import type { SubmittedOrder } from '@mypoz/shared';
import { availableOrderActions, isOrderEditableFor, orderPayableCents } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, Icon } from '@mypoz/ui';

import { OrderDetailPanel } from './OrderDetailPanel.js';
import { OrderProgress } from './OrderProgress.js';
import { OrderStatusChip } from './OrderStatusChip.js';
import { PayFromWalletDialog } from './PayFromWalletDialog.js';
import { actionLinkClass, buttonClass } from '@/lib/styles.js';
import { editOrderRoute } from '@/shell/navigation.js';
import { useFormat } from '@/lib/format.js';
import { startDownload } from '@/lib/download.js';
import { useOrders } from '@/features/orders/context.js';
import { usePackages } from '@/features/packages/index.js';
import { useToast } from '@/features/toast/index.js';

export interface OrderCardProps {
  order: SubmittedOrder;
  /**
   * True when this is the one order a notification's `resolveNotificationLink` (MYP-68) named via
   * `?order=<orderNumber>` — the card starts open and scrolls itself into view. Not a controlled
   * `open` prop: once opened this way, the card's own `open` state still owns whether it's expanded,
   * so a manual collapse afterward sticks even though the URL still names this order.
   */
  autoOpen?: boolean;
}

export function OrderCard({ order, autoOpen }: OrderCardProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const toast = useToast();
  const { cancel, confirmReceipt, loadDetail, requestInvoice } = useOrders();
  const { reload: reloadPackages } = usePackages();
  const [open, setOpen] = useState(() => autoOpen ?? false);
  const [unavailable, setUnavailable] = useState(false);
  const [paying, setPaying] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelProblem, setCancelProblem] = useState<'refused' | 'notCancelled' | 'unknown' | null>(
    null,
  );
  const [askingReceipt, setAskingReceipt] = useState(false);
  const [receiving, setReceiving] = useState(false);
  const [receiveProblem, setReceiveProblem] = useState<
    'refused' | 'notReceived' | 'unknown' | null
  >(null);
  const [requestingInvoice, setRequestingInvoice] = useState(false);
  /**
   * The last invoice link handed to the hidden frame, kept on the card as a backup the customer can
   * open themselves. The frame cannot report whether a download actually started (a blocked frame,
   * or a sign-in page instead of the PDF — api-issues #38), so the card never claims more than
   * "should start" and always offers the link.
   */
  const [invoiceUrl, setInvoiceUrl] = useState<string | null>(null);
  const panelId = useId();
  const cardRef = useRef<HTMLDivElement>(null);

  // Covers arriving at an already-mounted Orders page pointed at a *different* order (the initial
  // `useState` above only runs once, on mount) — opens this card and brings it into view without
  // re-opening it every render: the effect only re-fires when `autoOpen` itself flips to true.
  useEffect(() => {
    if (!autoOpen) return;
    setOpen(true);
    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [autoOpen]);

  /*
    Detail is fetched the first time this card is expanded, never with the list (MYP-53). A summary
    row carries no packages, no address and no timeline, and the account has hundreds of orders —
    asking for all of them up front is hundreds of requests to render one screen.

    `loadDetail` is idempotent and keeps its own in-flight set, so collapsing and re-opening a card
    costs nothing and this effect needs no guard of its own.
  */
  useEffect(() => {
    if (open) loadDetail(order.orderNumber);
  }, [open, order.orderNumber, loadDetail]);

  /*
    Guards the completion of the invoice and confirm-receipt requests, not the requests themselves
    — neither can be called back once sent, so all that is guarded is a `setState` on a card that
    might not be here to receive it.
  */
  const mounted = useRef(true);
  useEffect(() => {
    // Set on every mount, not just declared at init - StrictMode's dev-only mount, cleanup,
    // remount cycle runs this cleanup once before the component's real life begins, and a ref
    // that only ever goes false would come out of that cycle stuck there.
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Confirm receipt (MYP-93): asked first, because it cannot be undone, then sent once. A refusal
   * or a lost reply is shown under the buttons in the customer's language; the vendor's own text
   * only goes to the log. A lost reply that the re-read shows did land counts as done.
   */
  const handleReceiveConfirmed = () => {
    setAskingReceipt(false);
    setReceiving(true);
    setReceiveProblem(null);
    void confirmReceipt(order.orderNumber).then((outcome) => {
      if (!mounted.current) return;
      setReceiving(false);
      if (outcome.kind === 'received') {
        toast.show(t('orders.card.receivedToast', { orderNumber: order.orderNumber }));
      } else if (outcome.kind === 'refused') {
        setReceiveProblem('refused');
      } else if (outcome.kind === 'unconfirmed') {
        if (outcome.nowReceived === true) {
          toast.show(t('orders.card.receivedToast', { orderNumber: order.orderNumber }));
        } else {
          setReceiveProblem(outcome.nowReceived === false ? 'notReceived' : 'unknown');
        }
      }
    });
  };

  /**
   * Cancel (MYP-94): asked first, then sent once. Cancelling releases the order's packages, so on
   * success the package list is fetched again too, or Packages and Step 1 of New order would keep
   * hiding them. A lost reply that the re-read shows did land counts as done.
   */
  const handleCancelConfirmed = () => {
    setConfirmingCancel(false);
    setCancelling(true);
    setCancelProblem(null);
    void cancel(order.orderNumber).then((outcome) => {
      const done =
        outcome.kind === 'cancelled' ||
        (outcome.kind === 'unconfirmed' && outcome.nowCancelled === true);
      if (done) reloadPackages();
      if (!mounted.current) return;
      setCancelling(false);
      if (done) {
        toast.show(t('orders.card.cancelledToast', { orderNumber: order.orderNumber }));
      } else if (outcome.kind === 'refused') {
        setCancelProblem('refused');
      } else if (outcome.kind === 'unconfirmed') {
        setCancelProblem(outcome.nowCancelled === false ? 'notCancelled' : 'unknown');
      }
    });
  };

  /*
    MYP-124: ask the vendor for the link, then download it in place (`startDownload`) — the link
    answers with the PDF as an attachment, so nothing opens and the page stays as it was. A second
    click while waiting does nothing: the button is disabled and the ref guards the gap before it.
  */
  const invoiceInFlight = useRef(false);
  const handleInvoice = () => {
    if (invoiceInFlight.current) return;
    invoiceInFlight.current = true;
    setRequestingInvoice(true);
    void requestInvoice(order.orderNumber).then((outcome) => {
      invoiceInFlight.current = false;
      if (outcome.kind === 'ready') startDownload(outcome.url);
      if (!mounted.current) return;
      setRequestingInvoice(false);
      setInvoiceUrl(outcome.kind === 'ready' ? outcome.url : null);
      toast.show(
        outcome.kind === 'ready'
          ? t('orders.card.invoiceDownloading')
          : outcome.kind === 'notFound'
            ? t('orders.card.invoiceNotFound')
            : t('orders.card.invoiceFailed'),
      );
    });
  };

  /**
   * The whole summary is a click target, not just the Details link.
   *
   * Two things it must not do. It must not fire when the click landed on a control of its own —
   * Pay, Edit, Details all handle themselves, and toggling as well would undo them. And it must
   * not fire after a drag: selecting an order number to copy it should leave the card alone.
   *
   * Deliberately mouse-only convenience. The row holds buttons, so it cannot itself be a button
   * without nesting interactive elements; the real Details control below keeps aria-expanded and
   * remains what a keyboard or screen reader drives.
   */
  const toggleFromSurface = (event: MouseEvent<HTMLDivElement>) => {
    if (
      event.target instanceof Element &&
      event.target.closest('button, a, input, select, label')
    ) {
      return;
    }
    if (window.getSelection()?.toString()) return;

    setOpen((wasOpen) => !wasOpen);
  };

  const actions = availableOrderActions(order.status);
  // The vendor's own total where it has one — see `billedTotalCents` for why a sum of the lines
  // is not the same number on every order, and why the difference is the customer's money.
  const total = orderPayableCents(order);

  return (
    <Card className="p-0">
      <div
        ref={cardRef}
        onClick={toggleFromSurface}
        // scroll-mt-20 clears the sticky header (h-16, see Layout.tsx) with a little breathing
        // room, so MYP-68's auto-scroll-into-view doesn't land this row's own heading underneath
        // it — only the `autoOpen` scroll target needs this, but it costs nothing to apply always.
        className="cursor-pointer scroll-mt-20 px-5 py-4 transition-colors hover:bg-surface-alt/60 sm:px-6"
      >
        {/*
          Three columns, not two lines of flowing text. The status column is fixed-width so chips
          and progress dots line up down the whole list — let them flow after the channel name and
          a long one shoves them sideways, which is the opposite of scannable.
        */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-6">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-ink tabular-nums">{order.orderNumber}</p>
            <p className="mt-0.5 truncate text-sm text-muted">
              {/*
                The channel's own name, straight off the order (MYP-53). It used to be looked up
                in the fixture catalogue and the carrier appended beside it; the vendor gives one
                name for both, so printing it twice was the only thing that lookup added.
              */}
              {/*
                The package count only exists on the detail record, which is not fetched until the
                card is opened. Left off rather than printed as "0 packages": a count of zero is a
                statement about the order, and it would be a false one on every unopened row.
              */}
              {[
                order.carrier,
                order.packages.length > 0
                  ? t('orders.card.packageCount', { count: order.packages.length })
                  : undefined,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>

          <div className="flex shrink-0 flex-col gap-1.5 sm:w-44">
            <OrderStatusChip status={order.status} label={order.statusLabel} />
            <OrderProgress status={order.status} />
          </div>

          <div className="shrink-0 sm:text-right">
            {total === undefined ? (
              <>
                <p className="text-xl leading-none font-semibold text-muted">—</p>
                <p className="mt-1 text-xs text-muted">{t('orders.card.pricePending')}</p>
              </>
            ) : (
              <p className="text-xl leading-none font-semibold text-ink tabular-nums">
                {fmt.money(total, order.currency)}
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          {askingReceipt ? (
            <>
              <p className="text-sm text-ink">{t('orders.card.receiveConfirm')}</p>
              <button
                type="button"
                onClick={() => setAskingReceipt(false)}
                className={buttonClass('secondary')}
              >
                {t('orders.card.receiveBack')}
              </button>
              <button
                type="button"
                onClick={handleReceiveConfirmed}
                className={buttonClass('primary')}
              >
                {t('orders.card.receiveConfirmed')}
              </button>
            </>
          ) : confirmingCancel ? (
            <>
              <p className="text-sm text-ink">{t('orders.card.cancelConfirm')}</p>
              <button
                type="button"
                onClick={() => setConfirmingCancel(false)}
                className={buttonClass('secondary')}
              >
                {t('orders.card.keepOrder')}
              </button>
              <button
                type="button"
                onClick={handleCancelConfirmed}
                className={buttonClass('danger')}
              >
                {t('orders.card.cancelConfirmed')}
              </button>
            </>
          ) : (
            actions.map((action) =>
              /*
                Edit is the one action that goes somewhere. A <Link> rather than a button because it
                is a navigation — middle-click and copy-link should work on it like any other.
              */
              action === 'edit' && isOrderEditableFor(order) ? (
                <Link
                  key={action}
                  to={editOrderRoute(order.orderNumber)}
                  className={buttonClass('secondary')}
                >
                  {t('orders.card.action.edit')}
                </Link>
              ) : action === 'pay' ? (
                <button
                  key={action}
                  type="button"
                  onClick={() => setPaying(true)}
                  className={buttonClass('primary')}
                >
                  {t('orders.card.action.pay')}
                </button>
              ) : action === 'confirmReceipt' ? (
                <button
                  key={action}
                  type="button"
                  onClick={() => {
                    setReceiveProblem(null);
                    setAskingReceipt(true);
                  }}
                  disabled={receiving}
                  className={buttonClass(
                    'primary',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  {receiving ? t('orders.card.confirming') : t('orders.card.action.confirmReceipt')}
                </button>
              ) : action === 'cancel' ? (
                <button
                  key={action}
                  type="button"
                  onClick={() => {
                    setCancelProblem(null);
                    setConfirmingCancel(true);
                  }}
                  disabled={cancelling}
                  className={actionLinkClass('disabled:cursor-not-allowed disabled:opacity-50')}
                >
                  {cancelling ? t('orders.card.cancelling') : t('orders.card.action.cancel')}
                </button>
              ) : action === 'invoice' ? (
                <button
                  key={action}
                  type="button"
                  onClick={handleInvoice}
                  disabled={requestingInvoice}
                  className={buttonClass(
                    'secondary',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                  )}
                >
                  {requestingInvoice
                    ? t('orders.card.invoiceRequesting')
                    : t('orders.card.action.invoice')}
                </button>
              ) : (
                // action === 'track' — the one action still with nothing wired behind it.
                <button
                  key={action}
                  type="button"
                  onClick={() => setUnavailable(true)}
                  className={actionLinkClass()}
                >
                  {t(`orders.card.action.${action}`)}
                </button>
              ),
            )
          )}

          <button
            type="button"
            onClick={() => setOpen((wasOpen) => !wasOpen)}
            aria-expanded={open}
            aria-controls={panelId}
            className={actionLinkClass('ml-auto')}
          >
            {open ? t('orders.card.hide') : t('orders.card.details')}
            <Icon name={open ? 'expand_less' : 'expand_more'} size={18} />
          </button>
        </div>

        {/*
          Cancelling disappears once the warehouse has packed. Saying why beats silently dropping
          the button — a control that vanishes between two visits reads as a bug, not a rule.
        */}
        {order.status === 'unpaid' ? (
          <p className="mt-4 border-l-2 border-brand pl-3 text-sm text-ink">
            {t('orders.card.notice.unpaid')}
          </p>
        ) : null}

        {cancelProblem ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {t(`orders.card.cancelProblem.${cancelProblem}`)}
          </p>
        ) : null}

        {receiveProblem ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {t(`orders.card.receiveProblem.${receiveProblem}`)}
          </p>
        ) : null}

        {unavailable ? (
          <p className="mt-3 text-sm text-muted">{t('orders.card.actionUnavailable')}</p>
        ) : null}
        {invoiceUrl ? (
          <p role="status" className="mt-3 text-sm text-muted">
            {t('orders.card.invoiceFallback')}{' '}
            <a
              href={invoiceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={actionLinkClass('inline-flex')}
            >
              {t('orders.card.invoiceOpen')}
            </a>
          </p>
        ) : null}
      </div>

      <div id={panelId} hidden={!open}>
        {open ? (
          <>
            <OrderDetailPanel order={order} onUnavailable={() => setUnavailable(true)} />

            {/*
              A second Hide at the foot of the panel. The detail runs to six sections, and closing
              it should not mean scrolling back past all of them to reach the control you opened it
              with.
            */}
            <div className="flex justify-end border-t border-line px-5 py-3 sm:px-6">
              <button type="button" onClick={() => setOpen(false)} className={actionLinkClass()}>
                {t('orders.card.hide')}
                <Icon name="expand_less" size={18} />
              </button>
            </div>
          </>
        ) : null}
      </div>

      {/*
        Rendered only while open, so the dialog's focus trap and scroll lock exist exactly as long
        as the dialog does — and so closing it unmounts the payment password along with everything else.
      */}
      {paying ? <PayFromWalletDialog order={order} onClose={() => setPaying(false)} /> : null}
    </Card>
  );
}
